import {
    TActivePlanCode,
    TPlanCode,
    TPricingPeriod,
    TPricingTier
} from '~/components/Membership/types/membership.types';
import {
    PRICING_PLANS,
    pricingTiersPlanCodes
} from '~/components/Membership/constants/membershipData';
import {
    ICouponResponse,
    TDiscountType,
    TLimitedPeriodUnit
} from '~/types/server.types';
import {
    FLEXIBLE_COUPONS,
    TCodePerPlan,
    PRICE_CHANGE_DATE
} from '~/components/Membership/constants/flexibleCoupons';
import { ISubscription } from '~/types/model.types';
import dayjs from 'dayjs';
import { Coupons } from '~/constants/coupons';

export type TPlanPrice = {
    full: string;
    integer: string;
    decimal?: string;
};

export const formatPrice = (price: string | number): TPlanPrice => {
    const shouldShowPrice =
        (price && !isNaN(+price) && +price !== 0) || Number(price) === 0;

    if (!shouldShowPrice) {
        return { integer: '', full: '' };
    }

    const num = typeof price === 'string' ? Number(price) : price;
    const hasDecimal = num - Math.floor(num) !== 0;
    const numToDisplay = num.toFixed(hasDecimal ? 2 : 0);

    return {
        integer: numToDisplay.split('.')[0],
        decimal: numToDisplay.split('.')[1],
        full: numToDisplay
    };
};

export const getDiscountedPrice = (
    price: string,
    discount: number
): TPlanPrice => {
    const number = parseFloat(price);
    if (!number || isNaN(number)) {
        return { integer: '', full: '' };
    }
    const reducedNumber = number * discount;
    return formatPrice(reducedNumber);
};

export function getPricingTier(planCode: TPlanCode): TPricingTier {
    return PRICING_PLANS[planCode]?.pricingTier || 'legacy';
}

export const isLegacyPlan = (planCode: TPlanCode) => {
    if (!planCode) {
        return false;
    }
    return getPricingTier(planCode) === 'legacy';
};

export const getPricingPeriod = (planCode: TPlanCode): TPricingPeriod => {
    if (!planCode) {
        return;
    }
    if (isLegacyPlan(planCode)) {
        return planCode.includes('monthly') ? 'monthly' : 'annual';
    }

    return PRICING_PLANS[planCode].period;
};

export const getOppositePeriod = (plan: TPlanCode): TActivePlanCode => {
    const oppositePlans = {
        starter_monthly_2023: 'starter_annual',
        starter_annual: 'starter_monthly_2023',
        essentials_monthly: 'essentials_annual',
        essentials_annual: 'essentials_monthly',
        premium_monthly: 'premium_annual',
        premium_annual: 'premium_monthly'
    };

    return oppositePlans[plan] || null;
};

export const getPricingPlanPerPeriod = (
    pricingTier: TPricingTier,
    pricingPeriod: TPricingPeriod
) => {
    const planCode = pricingTiersPlanCodes[pricingTier][pricingPeriod];
    return PRICING_PLANS[planCode];
};

export const getDiscountAmountWithUnit = (
    discountAmount: number,
    discountType: TDiscountType
) => {
    if (!discountAmount || !discountType) {
        return;
    }

    if (discountType === 'percent') {
        return `${discountAmount}%`;
    }
    if (discountType === 'fixed') {
        return `$${discountAmount}`;
    }
};

export const getLimitedPeriodText = (
    periodAmount,
    periodUnit: TLimitedPeriodUnit,
    options: { showNumberForOne?: boolean } = {}
) => {
    if (!periodAmount || !periodUnit) {
        return '';
    }

    // We're sometimes using a trick of setting the annual plan duration in
    // Recurly to "2 days" instead of "1 year" to prevent the user from
    // redeeming the same coupon multiple times.
    if (periodUnit === 'day') {
        return options.showNumberForOne ? `1 year` : `year`;
    }

    if (periodAmount === 1) {
        return options.showNumberForOne
            ? `${periodAmount} ${periodUnit}`
            : `${periodUnit}`;
    }
    return `${periodAmount} ${periodUnit}s`;
};

export function getCouponCodeByPlanCode(
    couponCode: string,
    planCode: TPlanCode
) {
    if (!planCode) {
        return couponCode;
    }

    const isFlexibleCoupon = isCouponFlexible(couponCode);

    return isFlexibleCoupon
        ? FLEXIBLE_COUPONS[couponCode].codePerPlan[planCode]
        : couponCode;
}

export const getReferrerFullName = (referrer: {
    first_name: string;
    last_name: string;
}) => {
    if (!referrer) {
        return;
    }

    const { first_name, last_name } = referrer;

    if (!last_name) {
        return first_name;
    }

    return `${first_name} ${last_name}`;
};

export function getDefaultBannerPlanCode({
    codePerPlan,
    selectedPricingPeriod
}: {
    codePerPlan: TCodePerPlan;
    selectedPricingPeriod: TPricingPeriod;
}): TActivePlanCode {
    if (codePerPlan) {
        return Object.keys(codePerPlan)[0] as TActivePlanCode;
    }
    return selectedPricingPeriod === 'monthly'
        ? 'starter_monthly_2023'
        : 'starter_annual';
}

export function getParentCouponCode(couponCode: string) {
    // Check if it has a parent flexible coupon
    for (const flexibleCoupon in FLEXIBLE_COUPONS) {
        const codePerPlan = FLEXIBLE_COUPONS[flexibleCoupon].codePerPlan;

        for (const planCode in codePerPlan) {
            if (couponCode === codePerPlan[planCode]) {
                return flexibleCoupon;
            }
        }
    }

    // No parent flexible coupon
    return couponCode;
}

export function isCouponFlexible(couponCode: string) {
    return !!FLEXIBLE_COUPONS[couponCode];
}

export function isDowngradePlans(
    oldPlanCode: TPlanCode,
    newPlanCode: TPlanCode
) {
    if (!newPlanCode) {
        return false;
    }

    // the order of these plans matters, don't change unless you know what your doing!
    const newPlans = [
        'starter_monthly_2023',
        'starter_annual',
        'monthly_basic',
        'starter_monthly',
        'monthly',
        'unlimited_monthly',
        'essentials_monthly',
        'annual',
        'unlimited_annual',
        'essentials_annual',
        'premium_monthly',
        'premium_annual',
        'lifetime',
        'free'
    ];

    return newPlans.indexOf(newPlanCode) < newPlans.indexOf(oldPlanCode);
}

export const doPlansShareSameTier = (
    planCode1: TPlanCode,
    planCode2: TPlanCode
): boolean => {
    if (!planCode1 || !planCode2) {
        return false;
    }
    const keywords = ['premium', 'essentials', 'starter'];

    return keywords.some(
        keyword => planCode1.includes(keyword) && planCode2.includes(keyword)
    );
};

export function getCouponFullyRedeemedDate(
    planStartDate: string,
    couponDetails: ICouponResponse
) {
    const _planStartDate = new Date(planStartDate);
    const couponDurationInMonths =
        couponDetails?.temporal_unit === 'year'
            ? 12
            : couponDetails?.temporal_amount || 0;
    return new Date(
        _planStartDate.setMonth(
            _planStartDate.getMonth() + couponDurationInMonths
        )
    );
}

export const getCurrentPlanCode = (subscriptionPlanCode: TPlanCode) => {
    switch (subscriptionPlanCode) {
        case 'starter_monthly':
            return 'starter_monthly_2023';
        case 'monthly_basic':
            return 'starter_monthly_2023';
        case 'unlimited_annual':
            return 'essentials_annual';
        case 'annual':
            return 'essentials_annual';
        case 'unlimited_monthly':
            return 'essentials_monthly';
        case 'monthly':
            return 'essentials_monthly';
        default:
            return subscriptionPlanCode;
    }
};

// todo: remove applying the price update 20 coupon code in Feb 2026
export const shouldApplyPriceUpdateCoupon = (
    newPlanCode: TPlanCode,
    subscription: ISubscription,
    isNewPriceChangeCouponActive: boolean
) => {
    const planStartDate = subscription?.pricing_plan?.plan_start_date;
    const existingPlanCode = subscription?.pricing_plan?.plan_type;
    const newPricingPeriod = getPricingPeriod(newPlanCode);
    const oldPricingPeriod = getPricingPeriod(existingPlanCode);
    const isDowngrading = isDowngradePlans(existingPlanCode, newPlanCode);
    const price20Coupon =
        FLEXIBLE_COUPONS[Coupons.priceupdate20].codePerPlan[existingPlanCode];

    return (
        isNewPriceChangeCouponActive &&
        (!isDowngrading ||
            (newPricingPeriod === 'annual' && oldPricingPeriod === 'annual')) &&
        (!subscription?.coupon?.coupon_code ||
            subscription?.coupon?.coupon_code === price20Coupon) &&
        planStartDate &&
        dayjs(planStartDate).isBefore(PRICE_CHANGE_DATE)
    );
};

const pricingLevels = {
    starter: { feature_level: 'Starter', service_level: 'Standard' },
    essentials: { feature_level: 'Essentials', service_level: 'Standard' },
    premium: { feature_level: 'Premium', service_level: 'Priority' },
    legacy: { feature_level: 'Essentials*', service_level: 'Standard' },
    lifetime: { feature_level: 'Premium', service_level: 'Standard' }
};

const planPricingLevelsMapping = {
    starter_monthly_2023: pricingLevels.starter,
    starter_annual: pricingLevels.starter,
    essentials_monthly: pricingLevels.essentials,
    essentials_annual: pricingLevels.essentials,
    premium_monthly: pricingLevels.premium,
    premium_annual: pricingLevels.premium,
    // Legacy
    starter_monthly: pricingLevels.legacy,
    monthly_basic: pricingLevels.legacy,
    monthly: pricingLevels.legacy,
    unlimited_monthly: pricingLevels.legacy,
    annual: pricingLevels.legacy,
    unlimited_annual: pricingLevels.legacy,
    lifetime: pricingLevels.lifetime,
    free: pricingLevels.legacy,
    manual: pricingLevels.legacy
};

export const getIntercomPricingLevel = (planCode: TPlanCode) => {
    return planPricingLevelsMapping[planCode] || null;
};
